import type { CSSProperties, FC, ReactNode } from 'react'

import { useRef } from 'react'
import { Check, ShieldCheck } from 'lucide-react'
import { useScrollProgress } from '@/hooks/use-scroll-progress'
import { PhoneFrame } from '@/components/generals/phone-frame'
import { SectionLabel } from '@/components/generals/section-label'

const BINDERS = [
    { label: 'HMS-håndbok', className: 'bg-night text-paper' },
    { label: 'Rutiner', className: 'bg-signal text-night' },
    { label: 'Skjemaer', className: 'bg-butter text-night' },
    { label: 'SJA', className: 'bg-night-raised text-paper' },
    { label: 'Avvik', className: 'bg-lime text-night' }
]

const STEPS = [
    {
        title: 'Dokumentasjon',
        text: 'Håndbok, rutiner og skjemaer tilpasset bedriften din, laget etter kravene fra Arbeidstilsynet. Uten en eneste papirperm.'
    },
    {
        title: 'Sjekklister og SJA',
        text: 'Fyll ut på stedet med bilder. Ferdige bransjemaler, og alt fungerer uten dekning med automatisk synk.'
    },
    {
        title: 'Avvik',
        text: 'Meld avvik og uønskede hendelser på sekunder, med full oversikt som er klar for tilsyn.'
    },
    {
        title: 'Tilsynshjelp og revisjon',
        text: 'Hjelp fra varselet kommer til tilsynet er over, årlig revisjon, og et HMS-team du når på telefon.'
    }
]

const stepFor = (progress: number): number => {
    if (progress < 0.3) return 0
    if (progress < 0.55) return 1
    if (progress < 0.8) return 2

    return 3
}

/** Five paper binders fold down into one phone as you scroll: the HMS folder, digitised. */
export const BinderScene: FC = (): ReactNode => {
    const track = useRef<HTMLElement>(null)
    const stage = useRef<HTMLDivElement>(null)
    const box = useRef<HTMLDivElement>(null)

    useScrollProgress(track, {
        startAt: 0,
        endAt: 1,
        onUpdate: progress => {
            const el = stage.current

            if (el) el.dataset.step = String(stepFor(progress))
        }
    })

    useScrollProgress(box, {
        startAt: 0.95,
        endAt: 0.25,
        varName: null,
        onUpdate: progress => {
            const el = box.current

            if (!el) return

            if (window.matchMedia('(min-width: 1024px) and (min-height: 700px) and (prefers-reduced-motion: no-preference)').matches) el.style.removeProperty('--p')
            else el.style.setProperty('--p', progress.toFixed(4))
        }
    })

    return (
        <section ref={track} id='alt-du-trenger' className='binder-track tone-paper relative' data-header='light'>
            <div ref={stage} className='binder-stage' data-step='0'>
                <div className='wrap grid h-full grid-cols-12 content-center gap-x-6 gap-y-12 pt-[var(--header-h)] pb-8'>
                    <div className='col-span-12 lg:col-span-5'>
                        <SectionLabel index='02'>Alt du trenger</SectionLabel>
                        <h2 className='t-h1 mt-5 balance'>Hele HMS-permen. I lomma.</h2>

                        <ol className='mt-10 border-t border-tone-line'>
                            {STEPS.map((step, i) => (
                                <li key={step.title} className='binder-step border-b border-tone-line py-4' data-index={i}>
                                    <p className='flex items-baseline gap-4'>
                                        <span className='t-label tabular text-tone-ink'>{String(i + 1).padStart(2, '0')}</span>
                                        <span className='t-h3'>{step.title}</span>
                                    </p>
                                    <div className='binder-step-body'>
                                        <p className='pt-2 pl-9 text-tone-muted pretty'>{step.text}</p>
                                    </div>
                                </li>
                            ))}
                        </ol>
                    </div>

                    <div className='col-span-12 lg:col-span-6 lg:col-start-7'>
                        <div ref={box} className='binder-box relative mx-auto aspect-[5/4] w-full max-w-[38rem] bg-mist' aria-hidden='true'>
                            <div className='absolute inset-x-[8%] bottom-[12%] h-px bg-night/30' />

                            <div className='absolute inset-x-[10%] bottom-[12%] flex h-[66%] items-end gap-3'>
                                {BINDERS.map((binder, i) => (
                                    <div
                                        key={binder.label}
                                        className={`binder ${binder.className}`}
                                        style={{ '--i': i } as CSSProperties}
                                    >
                                        <span className='binder-label'>{binder.label}</span>
                                        <span className='binder-ring' />
                                    </div>
                                ))}
                            </div>

                            <div className='binder-phone absolute left-1/2 top-[5%] w-[28%] max-w-[11rem]'>
                                <PhoneFrame>
                                    <div className='absolute inset-0 bg-paper px-3 pt-9 text-night'>
                                        <p className='t-label text-[0.5rem] text-slate'>HMS</p>
                                        <p className='mt-0.5 text-[0.8125rem] font-semibold leading-tight'>Din bedrift AS</p>

                                        <ul className='mt-3 flex flex-col gap-1.5'>
                                            {BINDERS.map((binder, i) => (
                                                <li
                                                    key={binder.label}
                                                    className='binder-row flex items-center justify-between bg-chalk px-2 py-1.5 text-[0.625rem] font-semibold'
                                                    style={{ '--i': i } as CSSProperties}
                                                >
                                                    {binder.label}
                                                    <Check className='size-3 text-[#3b7a1e]' strokeWidth={3} />
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </PhoneFrame>
                            </div>

                            <p className='binder-ready absolute bottom-[4%] left-1/2 flex -translate-x-1/2 items-center gap-2 whitespace-nowrap bg-night px-3 py-2 text-sm font-semibold text-paper'>
                                <ShieldCheck className='size-4 text-lime' />
                                Klar for tilsyn
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
